import React, {useState, useEffect, Fragment} from 'react';
import Login from 'js/reusable/auth/login';
import {AuthContext} from 'js/reusable/auth/authContext';
import {
	exchangeToken,
	getAccessToken,
	getAccessTokenWithFacebook,
	invalidateRefreshToken,
	invalidateRefreshTokenHeader,
	refreshAndGetToken,
} from 'js/reusable/auth/tool';
import {__} from 'js/tool/page/current';
import {certPath} from 'js/tool/APIProvider';
import NoCertificateFallback from 'js/reusable/auth/noCert';
import ServiceUnavailableFallback from 'js/reusable/auth/unvailableFallback';
import {FbPayload, LoginArgs, AuthResponse, ValidationErrors} from 'js/reusable/auth/types';

type IProps = {
	children: React.ReactNode;
}

const app = import.meta.env.REACT_APP_BUILD_TARGET;

export const isInKioskMode = () => {
	try {
		return window.self !== window.top;
	} catch (e) {
		return false;
	}
};

function AuthProvider({children}: IProps) {
	const [certInvalid, setCertInvalid]               = useState(false);
	const [certEmail, setCertEmail]                   = useState('');
	const [isLoggedIn, setIsLoggedIn]                 = useState(false);
	const [loading, setLoading]                       = useState(false);
	const [fbLoading, setFbLoading]                   = useState(false);
	const [initialRefreshing, setInitialRefreshing]   = useState(true);
	const [backendUnavailable, setBackendUnavailable] = useState(false);
	const [validationErrors, setValidationErrors]     = useState<AuthResponse | null>(null);

	function setStateFromLocalStorage() {
		setIsLoggedIn(!!localStorage.getItem('accessToken'));

		setValidationErrors(c => {
			const newError: ValidationErrors = {invalidationMessage: __(localStorage.getItem('invalidationMessage'))};

			if (!!c) {
				return {
					...c,
					...newError,
				};
			}

			return newError;
		});
	}

	async function getCertEmail() {
		if (app === 'admin') {
			try {
				const certEmail = await (await fetch(certPath, {credentials: 'include'})).json();

				if (certEmail && certEmail.email) {
					setCertEmail(certEmail.email);
				} else {
					setCertInvalid(true);
				}
			} catch (e) {
				setCertInvalid(true);
			}
		}
	}

	useEffect(() => {
		window.addEventListener('storage', setStateFromLocalStorage);

		return () => {
			window.removeEventListener('storage', setStateFromLocalStorage);
		};
	}, []);

	useEffect(() => {
		const exchangeKioskToken = async () => {
			if (isInKioskMode()) {
				// yes this is dirty, you're welcome to improve it
				const kioskAccessToken = location.search.replace('?accessToken=', '');

				if (!isLoggedIn && kioskAccessToken) {
					try {
						setInitialRefreshing(true);
						await exchangeToken(kioskAccessToken);
					} catch (e) {
						setBackendUnavailable(true);
					}

					setInitialRefreshing(false);
				}
			}
		};

		exchangeKioskToken();
	}, [isLoggedIn]);

	useEffect(() => {
		const onMount = async () => {
			if (isInKioskMode()) {
				return;
			}

			localStorage.removeItem('invalidationMessage');

			try {
				await getCertEmail();
				await refreshAndGetToken();
			} catch (e) {
				setBackendUnavailable(true);
			}

			setInitialRefreshing(false);
		};

		onMount();
	}, []);

	async function login({username, password, policy}: LoginArgs) {
		setLoading(true);

		const errors = await getAccessToken({username, password, policy});
		setValidationErrors(errors);

		setLoading(false);
	}

	async function fbLogin(fbPayload: FbPayload) {
		setFbLoading(true);

		const errors = await getAccessTokenWithFacebook(fbPayload);
		setValidationErrors(errors);

		setFbLoading(false);
	}

	async function logout() {
		setIsLoggedIn(false);
		await invalidateRefreshToken();
	}

	async function logoutNoRedirect() {
		await invalidateRefreshTokenHeader();
	}

	if (initialRefreshing) {
		return <Fragment />;
	}

	if (backendUnavailable) {
		return <ServiceUnavailableFallback />;
	}

	if (certInvalid) {
		return <NoCertificateFallback />;
	}

	return (
		<AuthContext.Provider value={{logoutNoRedirect, logout, login, fbLogin, isLoggedIn, certEmail}}>
			{
				isLoggedIn ?
					<div className="auth-content">{children}</div> :
					<Login
						loading={loading}
						fbLoading={fbLoading}
						validationErrors={validationErrors}
						certEmail={certEmail}
					/>
			}
		</AuthContext.Provider>
	);
}

export default AuthProvider;
